import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { logger } from '@zep/core-logger';
import { DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { CategoryInfoView, type CategoryInfoViewProps } from '../../features/category-select-view';
interface CategoryInfoModalProps extends CategoryInfoViewProps {
  onClose: () => void;
}

/**
 * CategoryInfoModal - 설정 페이지 전용 카테고리 정보 입력 모달
 *
 * 설정 페이지에서 카테고리 정보만 수정할 때 사용하는 간단한 모달입니다.
 * CategoryInfoView를 모달로 감싸며, 뒤로가기 버튼이 없습니다.
 *
 * @param formValues - build form 정보들
 * @param schoolInfoTree - BuildInfo의 학교 정보 트리
 * @param subjectTree - BuildInfo의 과목 트리
 * @param publishers - BuildInfo의 출판사 정보
 * @param userCountryCode - 사용자 국가 코드
 * @param onComplete - 교육정보 입력 완료 시 호출
 * @param onClose - 모달 닫기 시 호출
 */
const CategoryInfoModal = ({
  formValues,
  schoolInfoTree,
  subjectTree,
  publishers,
  userCountryCode,
  onComplete,
  onClose
}: CategoryInfoModalProps) => {
  const {
    t
  } = useTranslation(['build']);
  const title = t('build:schoolInfo.title') as string;
  return <ResponsiveDialog size="2xl" className="h-[90%] max-h-[800px] tablet-sm:gap-xs" onClose={() => onClose?.()} data-sentry-element="ResponsiveDialog" data-sentry-component="CategoryInfoModal" data-sentry-source-file="CategoryInfoModal.tsx">
      <DialogHeader className="mobile-lg:pt-md" title={title} hasCloseButton={true} onClose={() => onClose?.()} data-sentry-element="DialogHeader" data-sentry-source-file="CategoryInfoModal.tsx" />
      <main className="flex min-h-0 w-full flex-1 overflow-hidden">
        <div className="h-full w-full">
          <CategoryInfoView formValues={formValues} schoolInfoTree={schoolInfoTree} subjectTree={subjectTree} publishers={publishers} userCountryCode={userCountryCode} onComplete={onComplete} trigger="edit" data-sentry-element="CategoryInfoView" data-sentry-source-file="CategoryInfoModal.tsx" />
        </div>
      </main>
    </ResponsiveDialog>;
};

/**
 * 설정 페이지 카테고리 정보 모달 오픈 (overlay-kit)
 */
export const categoryInfoModal = {
  openAsync({
    formValues,
    schoolInfoTree,
    subjectTree,
    publishers,
    userCountryCode,
    onComplete
  }: Omit<CategoryInfoModalProps, 'onClose'>) {
    // 카테고리 정보 입력 모달 오픈 이벤트 트래킹 (설정에서 수정)
    logger.track('category_input_modal_open', {
      trigger: 'edit'
    });
    return overlay.openAsync(({
      unmount
    }) => <CategoryInfoModal formValues={formValues} schoolInfoTree={schoolInfoTree} subjectTree={subjectTree} publishers={publishers} userCountryCode={userCountryCode} onComplete={values => {
      onComplete(values);
      unmount();
    }} onClose={() => {
      unmount();
    }} />);
  }
};
export { CategoryInfoModal };